import { ImageIcon, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { useUpdateItemMutation } from "@/modules/mine";
import { FolderSelector } from "@/modules/mine/components/folder-selector";
import { TagSelector } from "@/modules/mine/components/tag-selector";
import type { Item, ItemTagData } from "@/modules/mine/types";
import type { DialogComponentProps } from "@/shared/components/dialog";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/ui/button";
import { DialogFooter } from "@/shared/ui/dialog";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Textarea } from "@/shared/ui/textarea";

type EditImageItemResult =
  | {
      action: "updated";
      itemId: string;
    }
  | {
      action: "deleted";
      itemId: string;
    };

interface EditImageItemProps extends DialogComponentProps<EditImageItemResult> {
  item: Item;
  onDelete?: (
    item: Item,
    resolve: (value: EditImageItemResult) => void,
  ) => void;
}

function formatImageSize(size?: string) {
  const bytes = Number(size);

  if (!Number.isFinite(bytes) || bytes <= 0) {
    return "未知大小";
  }

  if (bytes < 1024) {
    return `${bytes} B`;
  }

  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }

  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function getResolutionLabel(item: Item) {
  const width = item.image?.width ?? 0;
  const height = item.image?.height ?? 0;

  return `${width} × ${height}`;
}

function getImageFormatLabel(item: Item) {
  const mimeSubtype = item.mime_type?.split("/").at(1);

  if (mimeSubtype) {
    return mimeSubtype.replace(/\+xml$/i, "").toUpperCase();
  }

  return "图片";
}

export function EditImageItem({
  close,
  item,
  resolve,
}: EditImageItemProps) {
  const [title, setTitle] = useState(item.title ?? "");
  const [note, setNote] = useState(item.note ?? "");
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(
    item.folder_id ?? null,
  );
  const [tags, setTags] = useState<ItemTagData[]>(() => item.tags ?? []);
  const [fieldErrors, setFieldErrors] = useState<{ title?: string }>({});
  const updateItemMutation = useUpdateItemMutation();
  const paletteColors = useMemo(
    () => item.image?.visual?.palettes?.slice(0, 5) ?? [],
    [item.image?.visual?.palettes],
  );

  const handleSubmit = () => {
    const trimmedTitle = title.trim();

    if (!trimmedTitle) {
      setFieldErrors({ title: "请输入标题" });
      return;
    }

    setFieldErrors({});

    updateItemMutation.mutate(
      {
        id: item.id,
        title: trimmedTitle,
        note: note.trim(),
        folder_id: selectedFolderId || undefined,
        tags: {
          tag_ids: tags.map((tag) => tag.id),
        },
      },
      {
        onSuccess: () => {
          toast.success("保存成功");
          resolve({
            action: "updated",
            itemId: item.id,
          });
        },
      },
    );
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-[minmax(0,0.95fr)_minmax(340px,0.9fr)] gap-6">
        <div className="space-y-3">
          <div className="h-[320px] overflow-hidden rounded-xl bg-muted/30">
            {item.url ? (
              <img
                src={item.url}
                alt={item.title || "图片预览"}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full items-center justify-center text-muted-foreground">
                <ImageIcon className="size-10 opacity-30" />
              </div>
            )}
          </div>

          <div className="grid grid-cols-[repeat(3,minmax(0,1fr))_auto] gap-3 rounded-lg bg-muted/40 px-3 py-2.5 text-sm">
            <div>
              <p className="text-xs text-muted-foreground">尺寸</p>
              <p className="mt-1 font-medium">{getResolutionLabel(item)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">大小</p>
              <p className="mt-1 font-medium">{formatImageSize(item.size)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">格式</p>
              <p className="mt-1 font-medium">{getImageFormatLabel(item)}</p>
            </div>
            <div className="min-w-[88px]">
              <p className="text-xs text-muted-foreground">主色调</p>
              <div className="mt-1 flex items-center gap-1.5">
                {paletteColors.map((color) => (
                  <div
                    key={color.hex}
                    className="size-5 rounded-full"
                    style={{ backgroundColor: color.hex }}
                    title={`${color.hex} · ${Math.round(color.coverage * 100)}%`}
                  />
                ))}
                {paletteColors.length === 0 && (
                  <span className="font-medium text-muted-foreground">-</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="image-title" className="text-xs text-muted-foreground">
              标题
            </Label>
            <Input
              id="image-title"
              value={title}
              onChange={(event) => {
                setTitle(event.target.value);
                if (fieldErrors.title) {
                  setFieldErrors({});
                }
              }}
              placeholder="输入标题"
              aria-invalid={Boolean(fieldErrors.title)}
              className={cn(fieldErrors.title && "border-destructive")}
            />
            {fieldErrors.title ? (
              <p className="text-xs text-destructive">{fieldErrors.title}</p>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="image-note" className="text-xs text-muted-foreground">
              详细描述
            </Label>
            <Textarea
              id="image-note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="添加描述..."
              className="min-h-[120px] resize-none"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">所属文件夹</Label>
            <FolderSelector
              value={selectedFolderId}
              onChange={setSelectedFolderId}
              placeholder="未分类"
              allowEmpty={true}
              emptyLabel="未分类"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">标签</Label>
            <div className="flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1">
              <TagSelector
                value={tags}
                onChange={setTags}
                triggerClassName="bg-muted px-2 hover:bg-muted/80 [&_svg]:size-3"
              />
            </div>
          </div>
        </div>
      </div>

      <DialogFooter className="pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={close}
          disabled={updateItemMutation.isPending}
        >
          取消
        </Button>

        <Button type="button" onClick={handleSubmit} disabled={updateItemMutation.isPending}>
          {updateItemMutation.isPending && (
            <Loader2 className="mr-1.5 animate-spin" />
          )}
          确认
        </Button>
      </DialogFooter>
    </div>
  );
}
